import React, { useContext, useState } from 'react';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { formatExamDate } from '@/utils/helper';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { Button, Typography, useToast } from '@pw-tech/omni-ui';
import { Check, Clock, LaptopMinimal, NotebookPenIcon } from 'lucide-react';
import { useGetExamScheduleDates } from '@/modules/RegisterLayout/hooks/useGetExamScheduleDates';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { SOM_SYLLABUS, SOT_SYLLABUS } from '@/modules/common/constants';
import { RegistrationStatus } from '@/modules/common/enums';
const ExamDate = () => {
  const [selectedExamDate, setSelectedExamDate] = useState<string | null>(null);
  const {
    registrationDetails,
    submitRegisterForm,
    isFormSubmitting
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const {
    data: examScheduleDates
  } = useGetExamScheduleDates({
    enabled: true
  });
  const {
    toast: showToaster
  } = useToast();
  const importantInstructions = ['The CEE exam will be held online, so please ensure a stable internet connection.', 'Syllabus, exam pattern, and mock test link are provided above — prepare well and give it your best.', 'For any issues during the exam, feel free to contact our support team.'];
  const downloadSyllabus = () => {
    if (selectedCourseData?.batchCrmName.includes('SOT')) {
      window.open(SOT_SYLLABUS, '_blank');
    } else if (selectedCourseData?.batchCrmName.includes('SOB')) {
      window.open(SOM_SYLLABUS, '_blank');
    } else {
      showToaster({
        message: 'Syllabus not available for this course',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    showToaster({
      message: 'Syllabus downloaded successfully',
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      }
    });
  };
  const handleMettlRedirect = (link: string) => {
    if (!link) {
      showToaster({
        message: 'Link not available',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    window.open(link, '_blank');
  };
  return <article className="ml-2" data-sentry-component="ExamDate" data-sentry-source-file="ExamDate.tsx">
      <Typography variant="heading3" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
        {registrationDetails?.cee_status === RegistrationStatus.cee_missed ? <div className="mb-1">
            <p>
              Oops! You Have <span className="text-[#BF2734]">Missed</span> The Exam 😬
            </p>
            <Typography>Last chance to reschedule exam!</Typography>
          </div> : <span>Schedule Your Entrance Exam (CEE)</span>}
      </Typography>
      <div className="flex gap-2">
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
          <NotebookPenIcon size={16} data-sentry-element="NotebookPenIcon" data-sentry-source-file="ExamDate.tsx" />
          {selectedCourseData?.metaData?.totalQuestions || 46} questions
        </Typography>
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
          <Clock size={16} data-sentry-element="Clock" data-sentry-source-file="ExamDate.tsx" />
          {selectedCourseData?.metaData?.totalTime || 90} minutes
        </Typography>
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
          <LaptopMinimal size={16} data-sentry-element="LaptopMinimal" data-sentry-source-file="ExamDate.tsx" />
          {selectedCourseData?.metaData?.examMode || 'Online'}
        </Typography>
      </div>
      <div className="flex gap-3 my-4 sm:!mt-6">
        {examScheduleDates?.map((exam: any, index: number) => {
        const {
          day,
          date,
          month
        } = formatExamDate(exam.date);
        return <div key={exam.date} className={`w-[72px] rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.12)] cursor-pointer text-center items-center px-4 py-3 ${exam.date === selectedExamDate ? 'border-2 border-[#196CDC]' : ''}`} onClick={() => setSelectedExamDate(exam.date)}>
              <Typography variant="regular" className="!text-[#757575] text-sm">
                {day}
              </Typography>
              <Typography variant="heading2" weight="bold" className={exam.date === selectedExamDate ? '!text-[#196CDC]' : '!text-[#757575]'}>
                {date}
              </Typography>
              <Typography variant="regular" className="!text-[#757575] text-sm">
                {month}
              </Typography>
            </div>;
      })}
      </div>
      <Typography className="!text-[#757575] text-sm my-2" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
        You will receive the text link for the exam on your registered email ID
      </Typography>
      <div className="mt-4 p-2 sm:!p-4 bg-[#F8F8F8] rounded-lg">
        <div>
          <Typography variant="heading4" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="ExamDate.tsx">
            Important Instructions
          </Typography>
          <div className="pt-2 sm:!pt-[10px] flex flex-col gap-1">
            {importantInstructions.map((instruction, index) => <div key={index} className="flex gap-1">
                <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                  <Check size={20} color="#3D3D3D" />
                </div>
                <Typography className="!text-sm">{instruction}</Typography>
              </div>)}
          </div>
        </div>
        <div className="flex sm:!gap-4 pt-5">
          <Button type="button" variant="lowFocus" className="!text-[#196CDC]" onClick={() => handleMettlRedirect(registrationDetails?.mockTestUrl || '')} data-sentry-element="Button" data-sentry-source-file="ExamDate.tsx">
            Attempt Mock test
          </Button>
          <Button type="button" variant="link" className="!bg-[#F8F8F8]" onClick={() => downloadSyllabus()} data-sentry-element="Button" data-sentry-source-file="ExamDate.tsx">
            Download Full Syllabus
          </Button>
        </div>
      </div>
      <div className="h-8" />
      <div className="py-2">
        <FooterPortal label={selectedExamDate ? `Scheduled on ${formatExamDate(selectedExamDate).forSubmitBtnText}` : 'Select date'} type="button" btnWrapperClass="relative sm:left-[31%]" disabled={!selectedExamDate} onClick={() => {
        submitRegisterForm({
          ...registrationDetails,
          dateOfExamination: selectedExamDate
        }, true);
      }} isFormSubmitting={isFormSubmitting} data-sentry-element="FooterPortal" data-sentry-source-file="ExamDate.tsx" />
      </div>
    </article>;
};
export default ExamDate;